import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, CUIText, Icon, IconNames } from '@dotcom/crown-ui';
import { ButtonGroup } from '../../button-group/button-group';
import { ButtonGroupVariant } from '../../button-group/button-group-types';
import styles from './alignment-control.module.scss';

export type AlignmentValue = 'left' | 'center' | 'right';

const alignmentToIndex: Record<AlignmentValue, number> = {
  left: 0,
  center: 1,
  right: 2,
};

const indexToAlignment: Record<number, AlignmentValue> = {
  0: 'left',
  1: 'center',
  2: 'right',
};

export type AlignmentControlProps = {
  initialAlignment?: AlignmentValue;
  onChange: (alignment: AlignmentValue) => void;
  /** Optional label override; defaults to translated 'fontDrawer.alignment'. */
  label?: string;
  /** When false, suppresses the visible label above the controls. Defaults to true. */
  showLabel?: boolean;
  testId?: string;
};

/**
 * Standalone alignment button-group (left / center / right). Manages its own
 * preview state and notifies the parent via `onChange` for canvas updates.
 *
 * Has no canvas or context dependencies — drop it anywhere, including
 * Storybook.
 */
export const AlignmentControl = ({
  initialAlignment = 'left',
  onChange,
  label,
  showLabel = true,
  testId = 'alignment-control',
}: AlignmentControlProps): React.ReactElement => {
  const { t } = useTranslation();
  const alignmentLabel = label ?? (t('fontDrawer.alignment') as string);
  const buttons = [
    { icon: IconNames.AlignLeftBold, ariaLabel: t('fontDrawer.leftJustify') as string },
    { icon: IconNames.AlignCenterBold, ariaLabel: t('fontDrawer.centerJustify') as string },
    { icon: IconNames.AlignRightBold, ariaLabel: t('fontDrawer.rightJustify') as string },
  ];
  // eslint-disable-next-line security/detect-object-injection
  const defaultClickedIndex = alignmentToIndex[initialAlignment];

  return (
    <div className={styles['alignment-control']} data-testid={testId}>
      {showLabel && (
        <CUIText variant="form" as="label" className={styles.label}>
          {alignmentLabel}
        </CUIText>
      )}
      <ButtonGroup
        ariaLabel={alignmentLabel}
        defaultClickedIndex={defaultClickedIndex}
        variant={ButtonGroupVariant.Secondary}
        activeClassOverride={styles['selected']}
        onIndexChange={(index) => {
          // eslint-disable-next-line security/detect-object-injection
          onChange(indexToAlignment[index]);
        }}
      >
        {buttons.map(({ icon, ariaLabel }) => (
          <Button key={ariaLabel} aria-label={ariaLabel} size="icon" variant="secondary">
            <Icon name={icon} />
          </Button>
        ))}
      </ButtonGroup>
    </div>
  );
};
